import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { Input, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { sendVerificationCode, verifyCode } from '@/app/api/verification';
import { useToken } from '@/app/hooks/auth/token';
import { COLORS } from '@/app/constants/STYLES';
import { ThemeContext } from '@/theme/ThemeProvider';
import { MText } from '@/styles/mobilePageStyles';
interface EmailVerificationModalProps {
  currentEmail?: string;
  onVerify: (
    contact: string,
    code: string,
    token: string | null
  ) => Promise<void>;
  onSendCode: (email: string) => Promise<void>;
  onClose?: () => void;
}

const EmailVerificationModal = ({
  currentEmail,
  onVerify,
  onSendCode,
  onClose,
}: EmailVerificationModalProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(currentEmail || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [isSent, setIsSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: token } = useToken();

  const { isDarkMode, ...rest } = useContext(ThemeContext);
  const { textw, gray, PrimaryInputBorder, PrimaryInputBg } = rest.theme.COLORS;

  const handleSendCode = async () => {
    if (!email) {
      message.error(t('LANG_NO_INPUT_EMAIL'));
      return;
    }

    try {
      setIsLoading(true);
      await onSendCode(email);
      setIsSent(true);
      message.success(t('verificationCodeSent'));
    } catch (error) {
      message.error(t('failedToSendVerificationCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode) {
      message.error(t('verificationCodeError'));
      return;
    }

    try {
      setIsLoading(true);
      await onVerify(email, verificationCode, token);
      message.success(t('emailVerifiedSuccessfully'));
      onClose?.();
    } catch (error) {
      message.error(t('verificationCodeError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <InputGroup>
        <MText
          style={{
            fontSize: 14,
            fontWeight: '300',
          }}
        >
          {t('email')}
        </MText>
        <InputWrapper>
          <StyledInput
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@email.com"
            disabled={!!currentEmail || isLoading}
            textColor={textw}
            backgroundColor={PrimaryInputBg}
            borderColor={PrimaryInputBorder}
          />
          <Button
            onClick={handleSendCode}
            loading={isLoading}
            disabled={!email}
            style={{
              background: COLORS.primary,
              color: COLORS.white,
              fontSize: 14,
              minWidth: '80px',
              height: '100%',
              margin: 0,
              border: 'none',
              boxSizing: 'border-box',
              borderRadius: 4,
              padding: 0,
            }}
          >
            {t('sendCode')}
          </Button>
        </InputWrapper>
      </InputGroup>

      {isSent && (
        <InputGroup>
          <MText
            style={{
              fontSize: 14,
              fontWeight: '300',
            }}
          >
            {t('verificationCode')}
          </MText>
          <InputWrapper>
            <StyledInput
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter verification code"
              disabled={isLoading}
              textColor={textw}
              backgroundColor={PrimaryInputBg}
              borderColor={PrimaryInputBorder}
            />
            <Button
              type="primary"
              onClick={handleVerify}
              style={{
                background: COLORS.primary,
                color: COLORS.white,
                fontSize: 14,
                minWidth: '80px',
                height: '100%',
                margin: 0,
                border: 'none',
                boxSizing: 'border-box',
                borderRadius: 4,
                padding: 0,
              }}
              loading={isLoading}
              disabled={!verificationCode}
            >
              {t('verify')}
            </Button>
          </InputWrapper>
        </InputGroup>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2<{ color: string }>`
  margin-bottom: 24px;
  text-align: center;
  color: ${({ color }) => color};
`;

const InputGroup = styled.div``;

const Label = styled.div<{ color: string }>`
  margin-bottom: 8px;
  font-weight: 500;
  color: ${({ color }) => color};
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 6px;
  height: 40px;

  .ant-input {
    flex: 1;
  }
`;

const StyledInput = styled(Input)<{
  textColor: string;
  backgroundColor: string;
  borderColor: string;
}>`
  &.ant-input {
    color: ${({ textColor }) => textColor};
    background-color: ${({ backgroundColor }) => backgroundColor};
    border: ${({ borderColor }) => borderColor} 1px solid;
    outline: none;
    box-shadow: none;
    height: 100%;
    border-radius: 4px;

    &::placeholder {
      color: ${({ theme }) => theme.COLORS.text3};
    }

    &:hover,
    &:focus {
      border-color: ${({ theme }) => theme.COLORS.primary};
    }

    &:disabled {
      background-color: ${({ theme }) => theme.COLORS.disabled};
      color: ${({ theme }) => theme.COLORS.text3};
    }
  }
`;

export default EmailVerificationModal;
